// Category View screen - Category leaderboard and games
import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { Card, Leaderboard } from '../components';
import HamburgerMenu from '../components/HamburgerMenu';
import { getCategoryLeaderboard } from '../utils/leaderboard';
import { formatDateTime, groupByDate } from '../utils/dateHelpers';

const CategoryView = () => {
  const { competitionId, categoryId } = useParams();
  const navigate = useNavigate();
  const { players, categories, games, results } = useCompetition();

  const category = useMemo(() => {
    return categories.find(c => c.id === categoryId);
  }, [categories, categoryId]);

  const categoryGames = useMemo(() => {
    return games.filter(g => g.categoryId === categoryId && g.status !== 'voided');
  }, [games, categoryId]);

  const gamesByDate = useMemo(() => {
    return groupByDate(categoryGames);
  }, [categoryGames]);

  const leaderboard = useMemo(() => {
    console.log('CategoryView Debug:', {
      categoryId,
      playersCount: players.length,
      gamesCount: games.length,
      resultsCount: results.length,
      categoryGamesCount: games.filter(g => g.categoryId === categoryId).length,
      playedGamesCount: games.filter(g => g.categoryId === categoryId && g.status === 'played').length
    });
    const leaderboardData = getCategoryLeaderboard(players, games, results, categoryId);
    console.log('Leaderboard data:', leaderboardData);
    return leaderboardData;
  }, [players, games, results, categoryId]);

  const handleGameClick = (gameId) => {
    navigate(`/competition/${competitionId}/game/${gameId}`);
  };

  const handlePlayerClick = (playerId) => {
    navigate(`/competition/${competitionId}/player/${playerId}`);
  };

  if (!category) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-600">Category not found</p>
      </div>
    );
  }

  const getGameStatusBadge = (game) => {
    if (game.status === 'played') {
      return <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Played</span>;
    }
    return <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Planned</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <HamburgerMenu />
      {/* Header */}
      <div className="bg-white shadow" style={{ backgroundColor: `${category.color || '#3B82F6'}10` }}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="mb-2">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Category Leaderboard</p>
            <div className="flex items-center gap-3">
              <span className="text-5xl">{category.icon || '🎲'}</span>
              <h1 className="text-3xl font-bold text-gray-900">{category.name}</h1>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {categoryGames.filter(g => g.status === 'played').length} games played •{' '}
            {categoryGames.filter(g => g.status === 'planned').length} games planned
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Leaderboard */}
        <Card title="Category Leaderboard" className="mb-8">
          {leaderboard.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Player
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ranking Points
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Score
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leaderboard.map((score, index) => {
                    const player = players.find(p => p.id === score.playerId);
                    const getRankDisplay = (rank) => {
                      if (rank === 1) return '🥇';
                      if (rank === 2) return '🥈';
                      if (rank === 3) return '🥉';
                      return rank;
                    };

                    return (
                      <tr key={score.playerId} className={index < 3 ? 'bg-yellow-50' : ''}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {getRankDisplay(score.rank)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div
                            className="cursor-pointer hover:text-blue-600 font-medium"
                            onClick={() => handlePlayerClick(score.playerId)}
                          >
                            {score.playerName}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                          {score.score}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                          {score.actualScore?.toFixed(1) || '0.0'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No games played yet</p>
          )}
        </Card>

        {/* Games by Date */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Games</h2>
          {gamesByDate.length > 0 ? (
            <div className="space-y-6">
              {gamesByDate
                .slice()
                .reverse()
                .map(({ date, games: dateGames }) => (
                  <Card key={date}>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      {new Date(date).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </h3>
                    <div className="space-y-2">
                      {dateGames.map(game => (
                        <div
                          key={game.id}
                          onClick={() => handleGameClick(game.id)}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                        >
                          <div>
                            <p className="font-medium text-gray-900">{game.name}</p>
                            {game.startTime && (
                              <p className="text-xs text-gray-500 mt-1">{game.startTime}</p>
                            )}
                            {game.notes && (
                              <p className="text-xs text-gray-600 mt-1 line-clamp-1">{game.notes}</p>
                            )}
                          </div>
                          {getGameStatusBadge(game)}
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
            </div>
          ) : (
            <Card>
              <p className="text-gray-500 text-center py-4">No games scheduled for this category</p>
            </Card>
          )}
        </div>

        {/* Version Info */}
        <div className="text-center py-4 text-xs text-gray-400">
          v{require('../../package.json').version}
        </div>
      </div>
    </div>
  );
};

export default CategoryView;
